import React, { useState, createContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { initialize, getAllExperiments, whenAvailable } from '@vp/ab-reader';
import useSiteData from '../../hooks/useSiteData';
import {
  buildAvailableOptimizelyExperiments,
  buildActiveOptimizelyExperiments,
} from '../../optimizely/optimizelyExperimentsUtils';
import { supportedExperiments } from '../../optimizely/optimizelyExperiments';

/**
 * This context provides a wrapper that manages the optimizely experiments for the application.
 * The expected structure of a single activeOptimizelyExperiment is:
  {    
    experimentKey: string,  // the unique experimentKey identifier.
    variationKey: string,  // optional, the variationKey selected for the experiment.
    availableVariationKeys: [string], // the available variationKeys for the experiment.
    shouldFireImpression: bool // whether or not an impression should be fired when using this experiment.
                               // Notably, experiments that are not enabled for the site, not present for the current user, or have been modified via debug overrides will be false.
  }
 */
const OptimizelyExperimentsContext = createContext(null);

const OptimizelyExperimentsProvider = props => {
  const { children } = props;
  // Array of experimentKeys enabled for this site.
  const { enabledExperimentKeys } = useSiteData();

  // Array of experiment objects for the given user as determined by @vp/ab-reader.
  const [assignedOptimizelyExperiments, setAssignedOptimizelyExperiments] =
    useState([]);

  // Array of experiments that are available for the application as determined by the supportedExperiments, enabledExperimentKeys, and assignedOptimizelyExperiments.
  // See buildAvailableOptimizelyExperiments for more details.
  const [availableOptimizelyExperiments, setAvailableOptimizelyExperiments] =
    useState([]);

  // Array of experiments that can override an experiment in availableOptimizelyExperiments.
  const [
    debugOptimizelyExperimentOverrides,
    setDebugOptimizelyExperimentOverrides,
  ] = useState([]);

  // Array of experiments exposed to the rest of the application. Contains the result of applying the debug overrides to the available experiments.
  const [activeOptimizelyExperiments, setActiveOptimizelyExperiments] =
    useState([]);

  useEffect(() => {
    if (typeof window !== `undefined`) {
      // initialize the ab-reader, required after v3.x of ab-reader
      initialize();
      whenAvailable(async available => {
        // defaulted to 1000ms per ab-reader README example: https://gitlab.com/vistaprint-org/merchandising-technology/experimentation-platform/ab-libraries/ab-reader-js/-/tree/main
        if (available) {
          setAssignedOptimizelyExperiments(getAllExperiments());
        }
      }, 1000);
    }
  }, []);

  useEffect(
    () =>
      setAvailableOptimizelyExperiments(
        buildAvailableOptimizelyExperiments({
          supportedExperiments,
          siteConfigurationEnabledExperimentKeys: enabledExperimentKeys,
          assignedOptimizelyExperiments,
        })
      ),
    [enabledExperimentKeys, assignedOptimizelyExperiments]
  );

  useEffect(
    () =>
      setActiveOptimizelyExperiments(
        buildActiveOptimizelyExperiments({
          availableOptimizelyExperiments,
          debugOptimizelyExperimentOverrides,
        })
      ),
    [availableOptimizelyExperiments, debugOptimizelyExperimentOverrides]
  );

  const optimizellyExperimentsContextValues = useMemo(() => {
    function overrideOptimizelyExperiment({
      experimentKey,
      variationKey,
      availableVariationKeys,
    }) {
      const debugOptimizelyExperimentOverridesClone = [
        ...debugOptimizelyExperimentOverrides,
      ];
      const overrideExperimentIndex =
        debugOptimizelyExperimentOverridesClone.findIndex(
          experiment => experiment.experimentKey === experimentKey
        );

      // feature override already exists, set the variation key and fireImpression state
      if (overrideExperimentIndex > -1) {
        debugOptimizelyExperimentOverridesClone[overrideExperimentIndex] = {
          ...debugOptimizelyExperimentOverridesClone[overrideExperimentIndex],
          variationKey,
          shouldFireImpression: false,
        };
        setDebugOptimizelyExperimentOverrides(
          debugOptimizelyExperimentOverridesClone
        );
        return;
      }

      // feature override does not exist
      debugOptimizelyExperimentOverridesClone.push({
        experimentKey,
        variationKey,
        availableVariationKeys,
        shouldFireImpression: false,
      });
      setDebugOptimizelyExperimentOverrides(
        debugOptimizelyExperimentOverridesClone
      );
    }

    return {
      overrideOptimizelyExperiment,
      activeOptimizelyExperiments,
    };
  }, [activeOptimizelyExperiments, debugOptimizelyExperimentOverrides]);

  return (
    <OptimizelyExperimentsContext.Provider
      value={optimizellyExperimentsContextValues}
    >
      {children}
    </OptimizelyExperimentsContext.Provider>
  );
};

OptimizelyExperimentsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export { OptimizelyExperimentsContext, OptimizelyExperimentsProvider };
